/**
 * Listening module reducer functions.
 *
 * Split reducers for managing different aspects of listening module state:
 * - Audio playback state
 * - Answer selection state
 * - Navigation and review state
 *
 * @module listeningReducers
 */

import { REVIEW_TIME_SECONDS } from '@/lib/constants';
import type { ListeningModuleState, ListeningModuleAction } from './useListeningModuleState';

/**
 * Audio playback state interface
 */
interface AudioState {
  isPlayingSectionTitleAudio: boolean;
  sectionTitleAudioCompleted: boolean;
  currentContextAudioIndex: number;
  completedContextAudioIndices: Set<number>;
  allContextAudiosCompleted: boolean;
  hasCompletedCurrentSectionAudio: boolean;
}

/**
 * Answer selection state interface
 */
interface AnswerState {
  selectedAnswers: Record<number, string>;
  struckAnswers: Record<number, string[]>;
  flaggedQuestions: number[];
  changedQuestionIds: Record<number, true>;
  activeQuestionId: number | null;
}

/**
 * Navigation and review state interface
 */
interface NavigationState {
  currentSectionIndex: number;
  isReviewPeriod: boolean;
  timeRemaining: number;
  notes: string;
  playsRemaining: number;
}

/**
 * Reducer for audio playback state.
 *
 * Handles all audio-related state updates including section title audio,
 * context audio completion, and audio state resets.
 */
function audioStateReducer(
  state: AudioState,
  action: ListeningModuleAction,
): AudioState {
  switch (action.type) {
    case 'RESET_AUDIO_STATE':
      if (action.isReviewPeriod) {
        return {
          ...state,
          isPlayingSectionTitleAudio: false,
          sectionTitleAudioCompleted: true,
          currentContextAudioIndex: 0,
          completedContextAudioIndices: new Set<number>(),
          allContextAudiosCompleted: action.audioMeta.contextAudiosCount === 0,
          hasCompletedCurrentSectionAudio: action.audioMeta.contextAudiosCount === 0 && !action.audioMeta.hasSectionTitleAudio,
        };
      }
      return {
        ...state,
        isPlayingSectionTitleAudio: action.audioMeta.hasSectionTitleAudio,
        sectionTitleAudioCompleted: !action.audioMeta.hasSectionTitleAudio,
        currentContextAudioIndex: 0,
        completedContextAudioIndices: new Set<number>(),
        allContextAudiosCompleted: action.audioMeta.contextAudiosCount === 0,
        hasCompletedCurrentSectionAudio: false,
      };

    case 'COMPLETE_SECTION_TITLE_AUDIO':
      return {
        ...state,
        isPlayingSectionTitleAudio: false,
        sectionTitleAudioCompleted: true,
        hasCompletedCurrentSectionAudio: action.audioMeta.contextAudiosCount === 0,
      };

    case 'COMPLETE_CONTEXT_AUDIO': {
      const newCompleted = new Set(state.completedContextAudioIndices);
      newCompleted.add(action.audioIndex);
      const allCompleted = newCompleted.size >= action.totalContextAudios;

      if (allCompleted) {
        return {
          ...state,
          completedContextAudioIndices: newCompleted,
          allContextAudiosCompleted: true,
          hasCompletedCurrentSectionAudio: true,
        };
      }

      return {
        ...state,
        completedContextAudioIndices: newCompleted,
        currentContextAudioIndex: action.audioIndex + 1,
      };
    }

    case 'CHANGE_SECTION':
      return {
        ...state,
        isPlayingSectionTitleAudio: action.preserveReviewPeriod ? false : action.audioMeta.hasSectionTitleAudio,
        sectionTitleAudioCompleted: action.preserveReviewPeriod || !action.audioMeta.hasSectionTitleAudio,
        currentContextAudioIndex: 0,
        completedContextAudioIndices: new Set<number>(),
        allContextAudiosCompleted: action.preserveReviewPeriod ? true : action.audioMeta.contextAudiosCount === 0,
        hasCompletedCurrentSectionAudio: action.preserveReviewPeriod,
      };

    case 'START_REVIEW_PERIOD':
      return {
        ...state,
        isPlayingSectionTitleAudio: false,
        sectionTitleAudioCompleted: true,
        currentContextAudioIndex: 0,
        completedContextAudioIndices: new Set<number>(),
        allContextAudiosCompleted: true,
      };

    case 'AUTO_ADVANCE_SECTION':
      return {
        ...state,
        isPlayingSectionTitleAudio: action.audioMeta.hasSectionTitleAudio,
        sectionTitleAudioCompleted: !action.audioMeta.hasSectionTitleAudio,
        currentContextAudioIndex: 0,
        completedContextAudioIndices: new Set<number>(),
        allContextAudiosCompleted: action.audioMeta.contextAudiosCount === 0,
        hasCompletedCurrentSectionAudio: false,
      };

    default:
      return state;
  }
}

/**
 * Reducer for answer selection state.
 *
 * Handles answer selection, strike toggling, flag toggling, and active question selection.
 */
function answerStateReducer(
  state: AnswerState,
  action: ListeningModuleAction,
): AnswerState {
  switch (action.type) {
    case 'SET_ANSWER': {
      const previous = state.selectedAnswers[action.questionId];
      const isRevision =
        previous !== undefined && previous !== null && previous !== action.answerId;
      const changedQuestionIds =
        isRevision && !state.changedQuestionIds[action.questionId]
          ? { ...state.changedQuestionIds, [action.questionId]: true as const }
          : state.changedQuestionIds;
      return {
        ...state,
        selectedAnswers: { ...state.selectedAnswers, [action.questionId]: action.answerId },
        changedQuestionIds,
      };
    }

    case 'TOGGLE_STRIKE': {
      const current = state.struckAnswers[action.questionId] || [];
      const newStrikes = current.includes(action.answerId)
        ? current.filter((id) => id !== action.answerId)
        : [...current, action.answerId];
      return {
        ...state,
        struckAnswers: { ...state.struckAnswers, [action.questionId]: newStrikes },
      };
    }

    case 'TOGGLE_FLAG': {
      const newFlagged = state.flaggedQuestions.includes(action.questionId)
        ? state.flaggedQuestions.filter((id) => id !== action.questionId)
        : [...state.flaggedQuestions, action.questionId];
      return {
        ...state,
        flaggedQuestions: newFlagged,
      };
    }

    case 'SET_ACTIVE_QUESTION':
      return {
        ...state,
        activeQuestionId: action.questionId,
      };

    case 'CHANGE_SECTION':
    case 'AUTO_ADVANCE_SECTION':
      return {
        ...state,
        activeQuestionId: null,
      };

    default:
      return state;
  }
}

/**
 * Reducer for navigation and review state.
 *
 * Handles section navigation, review period, timer, notes, and play count.
 */
function navigationStateReducer(
  state: NavigationState,
  action: ListeningModuleAction,
): NavigationState {
  switch (action.type) {
    case 'CHANGE_SECTION':
      return {
        ...state,
        currentSectionIndex: action.sectionIndex,
        isReviewPeriod: action.preserveReviewPeriod ? state.isReviewPeriod : false,
      };

    case 'UPDATE_NOTES':
      return {
        ...state,
        notes: action.notes,
      };

    case 'UPDATE_PLAY_COUNT':
      return {
        ...state,
        playsRemaining: Math.max(0, action.count),
      };

    case 'START_REVIEW_PERIOD':
      return {
        ...state,
        isReviewPeriod: true,
        timeRemaining: REVIEW_TIME_SECONDS,
      };

    case 'SET_TIME_REMAINING':
      return {
        ...state,
        timeRemaining: action.time,
      };

    case 'AUTO_ADVANCE_SECTION':
      return {
        ...state,
        currentSectionIndex: action.nextSectionIndex,
        isReviewPeriod: false,
      };

    default:
      return state;
  }
}

/**
 * Combines all listening module reducers into a single reducer.
 *
 * Each sub-reducer handles its own slice of state, and the results are merged.
 * This approach reduces cognitive complexity by separating concerns.
 *
 * @param state - Current listening module state
 * @param action - Action to dispatch
 * @returns New state with all reducer results merged
 */
export function combineListeningReducers(
  state: ListeningModuleState,
  action: ListeningModuleAction,
): ListeningModuleState {
  // Extract state slices
  const audioState: AudioState = {
    isPlayingSectionTitleAudio: state.isPlayingSectionTitleAudio,
    sectionTitleAudioCompleted: state.sectionTitleAudioCompleted,
    currentContextAudioIndex: state.currentContextAudioIndex,
    completedContextAudioIndices: state.completedContextAudioIndices,
    allContextAudiosCompleted: state.allContextAudiosCompleted,
    hasCompletedCurrentSectionAudio: state.hasCompletedCurrentSectionAudio,
  };

  const answerState: AnswerState = {
    selectedAnswers: state.selectedAnswers,
    struckAnswers: state.struckAnswers,
    flaggedQuestions: state.flaggedQuestions,
    changedQuestionIds: state.changedQuestionIds,
    activeQuestionId: state.activeQuestionId,
  };

  const navigationState: NavigationState = {
    currentSectionIndex: state.currentSectionIndex,
    isReviewPeriod: state.isReviewPeriod,
    timeRemaining: state.timeRemaining,
    notes: state.notes,
    playsRemaining: state.playsRemaining,
  };

  // Run each reducer
  const newAudioState = audioStateReducer(audioState, action);
  const newAnswerState = answerStateReducer(answerState, action);
  const newNavigationState = navigationStateReducer(navigationState, action);

  // Merge results
  return {
    ...state,
    ...newAudioState,
    ...newAnswerState,
    ...newNavigationState,
  };
}

